import React, { useState, useRef, useEffect } from 'react';

// Function to provide haptic feedback on mobile devices
const provideHapticFeedback = () => {
  if (navigator.vibrate) {
    navigator.vibrate(50); // Gentle 50ms vibration
  }
};

const StyledInteractivePoem = () => {
  // First and last lines of the poem
  const firstLine = "You see him in a coffee shop. He's gorgeous, but won't look up from his laptop.";
  const walkAwayLine = "You walk away. You never see him again.";
  const finalLastLine = "He walks away.\nYou never see him again.";
  
  // Array of stanzas that will appear one by one
  const allStanzas = [
    "You walk up to his table, trying to be “chill”. You exchange numbers.",
    
    "You eventually call him. He answers but is noncommital. You plan the date anyway.",
    
    "He brings a single red rose. The thorns are still attached. Where did he find a rose with the thorns still attached?",
    
    "At minigolf he points out the \"cute blonde\". You\'ve never been blonde and don\'t want to be. He insists \“blondes have more fun\”. You\'re certainly not having fun now.",
    
    "You invite him over to your place for dinner. He kind of helps clean up but refuses to wash the spoons.",
    
    "He looks at your bookshelf and insists that only terrible people read Lolita.",

    "It\'s his birthday and you make him a red velvet cake. He insists its fake because you dyed it red instead of relying on some chemical reaction witchcraft. You have a nice dinner at his place, even if you have to wash all his spoons.",

    "Your lease is up and you could pay half the rent if you moved in with him. He insists it will be so much fun. It\'s only logical, right?",

    "You meet his mom who floods the room with words. You say nothing all night. He doesn\'t notice.",

    "You\'re upset he never washes the spoon. He cleans the entire apartment to make it up to you - except the spoons.",

    "He insists on buying a leather couch even though you are a vegetarian. You try again to explain but no, you\'re the one who doesn\'t understand",

    "You again ask him to wash the spoons. He doesn\'t understand why it\'s such a big deal. Why can\'t you be more understanding like his last girlfriend?",

    "It\'s your birthday and he plans a romantic evening. He buys you flowers. Then you sit on the couch and watch his favorite movie.",

    "There\'s a spoon on the nightstand. WHY IS THERE A SPOON ON THE NIGHTSTAND? He declares you\'re too difficult."
  ];
  
  // State to keep track of how many stanzas to show
  const [stanzaCount, setStanzaCount] = useState(0);
  
  // State to track if we've walked away
  const [hasWalkedAway, setHasWalkedAway] = useState(false);
  
  // Function to handle button click
  const handleTryHarder = () => {
    if (stanzaCount < allStanzas.length) {
      provideHapticFeedback();
      setStanzaCount(stanzaCount + 1);
    }
  };
  
  // Function to walk away
  const handleWalkAway = () => {
    provideHapticFeedback();
    setHasWalkedAway(true);
  };
  
  // Function to restart the poem
  const handleRestart = () => {
    provideHapticFeedback();
    setStanzaCount(0);
    setHasWalkedAway(false);
  };
  
  // Check if poem has concluded
  const isPoemConcluded = hasWalkedAway || stanzaCount >= allStanzas.length;
  
  // Refs for scrolling
  const lastStanzaRef = useRef(null);
  
  // Effect for scrolling to the latest stanza
  useEffect(() => {
    if (lastStanzaRef.current) {
      lastStanzaRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
    }
  }, [stanzaCount, hasWalkedAway]);

  return (
    <div className="page-wrapper">
      <div className="poem-container" role="article" aria-label="Interactive poem titled The Coffee Shop">
        <h1>Fallacy</h1>        
        <div className="main-content">
          <div className="poem-content">
            {/* First line */}
            <p className="poem-line first-line">{firstLine}</p>
            
            {/* Revealed stanzas */}
            {allStanzas.slice(0, stanzaCount).map((stanza, index) => (
              <div 
                key={index} 
                className="stanza"
                ref={index === stanzaCount - 1 ? lastStanzaRef : null}
              >
                {stanza.split('\n').map((line, i) => (
                  <p key={i} className="poem-line">{line}</p>
                ))}
              </div>
            ))}
            
            {/* Last line - changes based on state */}
            {isPoemConcluded && (
              <div 
                className="last-stanza"
                ref={lastStanzaRef}
              >
                {hasWalkedAway ? (
                  <p className="poem-line last-line">{walkAwayLine}</p>
                ) : (
                  finalLastLine.split('\n').map((line, i) => (
                    <p key={i} className="poem-line last-line">{line}</p>
                  ))
                )}
              </div>
            )}
            
            {/* Start over button appears beneath the poem when concluded */}
            {isPoemConcluded && (
              <div className="restart-container">
                <button 
                  className="restart-button"
                  onClick={handleRestart}
                  aria-label="Start the poem over"
                >
                  Start Over
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="action-buttons-container">
          <div className="action-buttons">
            <button
              onClick={handleTryHarder}
              disabled={isPoemConcluded}
              className={`action-button try-button ${
                isPoemConcluded ? 'disabled' : ''
              }`}
              aria-label="Try harder to get his attention"
            >
              Try Harder
            </button>
            
            <button
              onClick={handleWalkAway}
              disabled={isPoemConcluded}
              className={`action-button walk-button ${
                isPoemConcluded ? 'disabled' : ''
              }`}
              aria-label="Walk away from the situation"
            >
              Walk Away
            </button>
          </div>
        </div>
        
        <style jsx>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Montserrat:wght@300;400;500&display=swap');
          
          .page-wrapper {
            display: flex;
            justify-content: center;
            width: 100%;
            min-height: 100vh;
            background-color: #f9f8f5;
          }
          
          .poem-container {
            font-family: 'Montserrat', sans-serif;
            background-color: #f9f8f5;
            color: #2c2c2c;
            padding: 40px 40px 100px 40px;
            min-height: 80vh;
            width: 100%;
            max-width: 800px;
            display: flex;
            flex-direction: column;
            position: relative;
          }
          
          h1 {
            text-align: center;
            font-family: 'Cormorant Garamond', serif;
            font-weight: 300;
            color: #2a2a2a;
            margin-bottom: 12px;
            font-size: 2.5rem;
            letter-spacing: 1.5px;
            text-transform: uppercase;
          }
          
          .subtitle {
            text-align: center;
            margin-bottom: 30px;
            color: #605e5c;
            font-weight: 300;
            font-size: 0.95rem;
            letter-spacing: 0.5px;
          }
          
          .main-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            margin-bottom: 20px;
            padding-bottom: 20px;
          }
          
          .poem-content {
            max-width: 600px;
            width: 100%;
            font-family: 'Cormorant Garamond', serif;
            padding: 0;
            margin: 0 auto;
            text-align: left;
          }
          
          .poem-line {
            margin-bottom: 0.7em;
            font-size: 1.2rem;
            color: #3c3c3c;
            line-height: 1.6;
            font-weight: 300;
            transition: all 0.5s ease;
            position: relative;
            animation: fadeInLine 1.2s cubic-bezier(0.19, 1, 0.22, 1);
            animation-fill-mode: both;
          }
          
          .first-line {
            font-style: italic;
            position: relative;
            padding-bottom: 1.2em;
          }
          
          .first-line:after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            width: 20px;
            height: 1px;
            background-color: rgba(141, 127, 108, 0.2);
          }
          
          .stanza {
            border-left: 2px solid rgba(141, 127, 108, 0.5);
            padding-left: 25px;
            margin: 25px 0;
            animation: fadeIn 0.8s cubic-bezier(0.19, 1, 0.22, 1);
          }
          
          .last-stanza {
            margin-top: 30px;
            font-style: italic;
            animation: fadeIn 0.8s cubic-bezier(0.19, 1, 0.22, 1);
          }
          
          .last-line {
            position: relative;
          }
          
          .last-line:last-child:after {
            content: "∎";
            position: relative;
            display: inline-block;
            font-size: 0.8rem;
            color: #8d7f6c;
            opacity: 0.5;
            margin-left: 0.5em;
          }
          
          .restart-container {
            display: flex;
            justify-content: center;
            margin-top: 40px;
            animation: fadeIn 1s ease 0.5s forwards;
            opacity: 0;
          }
          
          .restart-button {
            background: transparent;
            color: #3a3a3a;
            border: 1px solid #8d7f6c;
            border-radius: 4px;
            padding: 10px 24px;
            font-family: 'Montserrat', sans-serif;
            font-weight: 300;
            font-size: 0.9rem;
            letter-spacing: 1.5px;
            text-transform: uppercase;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
          }
          
          .restart-button:hover {
            background: rgba(141, 127, 108, 0.1);
            border-color: #8d7f6c;
            transform: translateY(-2px);
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
          }
          
          .restart-button:active {
            transform: translateY(0);
          }
          
          .action-buttons-container {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            background-color: rgba(249, 248, 245, 0.95);
            padding: 15px 0;
            box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
            z-index: 100;
            backdrop-filter: blur(4px);
          }
          
          .action-buttons {
            display: flex;
            justify-content: center;
            gap: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          
          .action-button {
            background-color: #f4f2eb;
            border: 1px solid rgba(141, 127, 108, 0.5);
            border-radius: 4px;
            padding: 12px 28px;
            font-family: 'Montserrat', sans-serif;
            font-weight: 400;
            font-size: 0.9rem;
            letter-spacing: 1px;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          }
          
          .action-button:hover {
            box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
            transform: translateY(-2px);
          }
          
          .action-button:active {
            transform: translateY(0px);
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          }
          
          /* Focus styles for accessibility */
          .action-button:focus-visible, 
          .restart-button:focus-visible {
            outline: 2px solid #8d7f6c;
            outline-offset: 2px;
          }
          
          .try-button {
            color: #3a3a3a;
            background-color: #f8f6f1;
            border-color: #8d7f6c;
          }
          
          .try-button:hover {
            background-color: #f1ede6;
          }
          
          .walk-button {
            color: #3a3a3a;
            background-color: #f0ece5;
            border-color: #a39684;
          }
          
          .walk-button:hover {
            background-color: #e9e3d9;
          }
          
          .action-button.disabled {
            opacity: 0.4;
            cursor: default;
            background-color: #f8f7f5;
            border-color: #d5d0c3;
            color: #a8a8a8;
            box-shadow: none;
            transform: none;
          }
          
          .action-button.disabled:hover {
            transform: none;
            box-shadow: none;
          }
          
          @keyframes fadeInLine {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @media (max-width: 768px) {
            .poem-container {
              padding: 20px 20px 90px 20px;
            }
            
            h1 {
              font-size: 2rem;
            }
            
            .poem-line {
              font-size: 1.1rem;
            }
            
            .action-buttons {
              flex-direction: row;
              gap: 12px;
              padding: 0 15px;
            }
            
            .action-button {
              padding: 12px 20px;
              font-size: 0.9rem;
              min-width: 130px;
              min-height: 48px; /* Larger touch target */
            }
            
            .action-buttons-container {
              padding: 15px 0;
            }
            
            .restart-button {
              padding: 12px 24px;
              font-size: 0.85rem;
              min-height: 48px; /* Larger touch target */
              min-width: 140px; /* Ensure enough space for text */
            }
          }
          
          /* Small phones optimization */
          @media (max-width: 380px) {
            .poem-container {
              padding: 15px 15px 90px 15px;
            }
            
            h1 {
              font-size: 1.8rem;
            }
            
            .action-buttons {
              gap: 8px;
            }
            
            .action-button {
              padding: 12px 16px;
              min-width: 120px;
            }
            
            .stanza {
              padding-left: 15px;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default StyledInteractivePoem;